import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, TextField, Grid, Card, CardContent,
  CardMedia, Chip, InputAdornment, Stack, Paper, Avatar,
} from '@mui/material';
import {
  Search, LocationOn, School, Shield, Payment, Group, Wifi, LocalParking,
  Bolt, Water, Star, ArrowForward, HomeWork, SupportAgent, SmartToy,
} from '@mui/icons-material';

const features = [
  { icon: <HomeWork fontSize="large" />, title: 'Find Accommodation', desc: 'Browse hundreds of verified properties near your campus, on or off campus.' },
  { icon: <Group fontSize="large" />, title: 'Room Pairing', desc: 'Find a compatible roommate to share accommodation and split costs.' },
  { icon: <Shield fontSize="large" />, title: 'Verified & Safe', desc: 'All properties and users go through KYC verification for your safety.' },
  { icon: <Payment fontSize="large" />, title: 'Secure Payments', desc: 'Pay rent and deposits securely through our platform with full transparency.' },
  { icon: <SupportAgent fontSize="large" />, title: 'Rent Control Support', desc: 'Government-backed dispute resolution ensures fair treatment for all.' },
  { icon: <SmartToy fontSize="large" />, title: 'AI-Powered', desc: 'Get smart property recommendations and instant support via AI assistant.' },
];

const stats = [
  { value: '10,000+', label: 'Listed Properties' },
  { value: '50,000+', label: 'Happy Students' },
  { value: '500+', label: 'Verified Landlords' },
  { value: '100+', label: 'Universities Covered' },
];

function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = () => {
    navigate(`/properties?query=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1, background: 'url("data:image/svg+xml,...") repeat' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '3.2rem' }, lineHeight: 1.2, mb: 2 }}>
                Find Your Perfect Student Accommodation
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 400, fontSize: { xs: '1rem', md: '1.15rem' } }}>
                Whether you're looking for on-campus hostels or off-campus apartments, CampusNest connects students with verified landlords for safe, affordable accommodation.
              </Typography>

              <Paper sx={{ p: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search by university, city or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment>,
                    sx: { '& fieldset': { border: 'none' } },
                  }}
                />
                <Button variant="contained" size="large" onClick={handleSearch} sx={{ px: 4, minWidth: { xs: '100%', sm: 'auto' }, borderRadius: 2 }}>
                  Search
                </Button>
              </Paper>

              <Stack direction="row" spacing={2} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip label="On Campus" icon={<School />} onClick={() => navigate('/properties?location_type=on_campus')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} />
                <Chip label="Off Campus" icon={<LocationOn />} onClick={() => navigate('/properties?location_type=off_campus')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} />
                <Chip label="Find Roommate" icon={<Group />} onClick={() => navigate('/dashboard/room-pairing')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 400, height: 350, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', border: '2px dashed rgba(255,255,255,0.3)' }}>
                  <HomeWork sx={{ fontSize: 120, opacity: 0.5 }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -4, position: 'relative', zIndex: 2 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight={800} color="primary">{stat.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom>
          Why Choose CampusNest?
        </Typography>
        <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          Everything you need to find, book, and manage your student accommodation in one platform.
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', p: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}>
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{feature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{feature.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works */}
      <Box sx={{ bgcolor: '#f0f4ff', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom>How It Works</Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>Simple steps to your new home</Typography>

          <Grid container spacing={4}>
            {[
              { step: '1', title: 'Search & Browse', desc: 'Search for accommodation near your campus. Filter by price, type, amenities and more.' },
              { step: '2', title: 'Book & Pair', desc: 'Book a room individually or find a roommate through our pairing system.' },
              { step: '3', title: 'Verify & Pay', desc: 'Complete KYC verification, sign your tenancy agreement and pay securely.' },
              { step: '4', title: 'Move In', desc: 'Get your keys and move into your new accommodation with full support.' },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box textAlign="center">
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem', fontWeight: 700, mx: 'auto', mb: 2 }}>
                    {item.step}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} gutterBottom>Ready to Find Your Nest?</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Join thousands of students who have found their perfect accommodation through CampusNest.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={Link} to="/register" variant="contained" size="large" endIcon={<ArrowForward />} sx={{ px: 4, py: 1.5 }}>
              Get Started Free
            </Button>
            <Button component={Link} to="/properties" variant="outlined" size="large" sx={{ px: 4, py: 1.5 }}>
              Browse Properties
            </Button>
          </Stack>

          <Box sx={{ mt: 6 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>Are you a landlord or agent?</Typography>
            <Button component={Link} to="/register" variant="text" color="primary">
              List your property on CampusNest <ArrowForward sx={{ ml: 0.5, fontSize: 16 }} />
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
